import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiJson, apiSend, apiUpload } from "@/api/client";
import {
  segment,
  withQuery,
  type ArchiveVariables,
  type SaveVariables,
  type Versioned,
} from "@/api/query";

/* Mirrors api/src/pbo_invoicing/settings/schemas.py. Dates are YYYY-MM-DD strings. */

export type TaxMode = "export_no_gst" | "export_lut";

export type CompanySettingsInput = {
  legal_name: string;
  short_name: string;
  address: string;
  cin: string;
  pan: string;
  gstin: string;
  email: string;
  phone: string;
  tax_mode: TaxMode;
  sac_code: string;
  signatory_name: string;
  signatory_title: string;
  default_payment_days: number;
  default_currency: string;
  realisation_months: number;
  /** Serial of the first invoice of a new financial year: 101 gives PBO/26-27/0101. */
  first_invoice_serial: number;
  /** GST registration threshold in rupees that applies to the company. */
  gst_threshold_inr: string;
  /** Planned rupee income a month; each bucket's budget is its share. Null = no budgets. */
  planned_monthly_income_inr: string | null;
  /** Days after the due date on which reminders 1, 2, 3... go; [] stops the daily reminders. */
  reminder_days: number[];
  /** The credit-note series prefix (CN gives CN/26-27/0001); the CA confirms it. */
  credit_note_prefix: string;
  /** Books: nothing may be dated on or before this day; null = no lock. */
  books_locked_through: string | null;
  /** The rate the P&L's income tax memo uses (25.17 = section 115BAA with surcharge and cess). */
  income_tax_percent: string;
};

export type CompanySettings = CompanySettingsInput & {
  logo_sha256: string | null;
  version: number;
  updated_at: string;
};

export type BankAccountInput = {
  label: string;
  account_name: string;
  bank: string;
  branch: string;
  account_no: string;
  swift: string;
  ifsc: string;
  iban: string;
  currency: string;
  is_default: boolean;
  confirmation_letter_sent: boolean;
  letter_sent_on: string | null;
};

export type BankAccount = BankAccountInput & Versioned & { archived_at: string | null };

export type LutInput = {
  fy_code: string;
  arn: string;
  filed_on: string;
  valid_from: string;
  valid_to: string;
};

export type Lut = LutInput & Versioned;

export const settingsKeys = {
  all: ["settings"] as const,
  company: ["settings", "company"] as const,
  logo: (sha256: string | null) => ["settings", "logo", sha256] as const,
  bankAccounts: ["settings", "bank-accounts"] as const,
  bankAccountList: (includeArchived: boolean) =>
    ["settings", "bank-accounts", { includeArchived }] as const,
  luts: ["settings", "lut"] as const,
};

// --------------------------------------------------------------------------- requests

export function getCompanySettings(): Promise<CompanySettings> {
  return apiJson<CompanySettings>("/settings/company");
}

export function updateCompanySettings(
  input: CompanySettingsInput,
  version: number,
): Promise<CompanySettings> {
  return apiSend<CompanySettings>("/settings/company", "PUT", input, version);
}

export async function getCompanyLogo(): Promise<Blob> {
  const res = await apiFetch("/settings/company/logo");
  return res.blob();
}

export function uploadCompanyLogo(file: File, version: number): Promise<CompanySettings> {
  const form = new FormData();
  form.append("file", file);
  return apiUpload<CompanySettings>("/settings/company/logo", "PUT", form, version);
}

export function listBankAccounts(includeArchived = false): Promise<BankAccount[]> {
  return apiJson<BankAccount[]>(
    withQuery("/settings/bank-accounts", { include_archived: includeArchived }),
  );
}

export function createBankAccount(input: BankAccountInput): Promise<BankAccount> {
  return apiSend<BankAccount>("/settings/bank-accounts", "POST", input);
}

export function updateBankAccount(
  id: string,
  input: BankAccountInput,
  version: number,
): Promise<BankAccount> {
  return apiSend<BankAccount>(`/settings/bank-accounts/${segment(id)}`, "PUT", input, version);
}

export function setBankAccountArchived(
  id: string,
  archived: boolean,
  version: number,
): Promise<BankAccount> {
  const action = archived ? "archive" : "unarchive";
  return apiSend<BankAccount>(
    `/settings/bank-accounts/${segment(id)}/${action}`,
    "POST",
    undefined,
    version,
  );
}

export function listLuts(): Promise<Lut[]> {
  return apiJson<Lut[]>("/settings/lut");
}

export function createLut(input: LutInput): Promise<Lut> {
  return apiSend<Lut>("/settings/lut", "POST", input);
}

export function updateLut(id: string, input: LutInput, version: number): Promise<Lut> {
  return apiSend<Lut>(`/settings/lut/${segment(id)}`, "PUT", input, version);
}

// --------------------------------------------------------------------------- hooks

export function useCompanySettings() {
  return useQuery({ queryKey: settingsKeys.company, queryFn: getCompanySettings });
}

export function useCompanyLogo(sha256: string | null | undefined) {
  return useQuery({
    queryKey: settingsKeys.logo(sha256 ?? null),
    queryFn: getCompanyLogo,
    enabled: Boolean(sha256),
    staleTime: Infinity,
  });
}

export function useSaveCompanySettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ input, version }: { input: CompanySettingsInput; version: number }) =>
      updateCompanySettings(input, version),
    onSuccess: (company) => queryClient.setQueryData(settingsKeys.company, company),
  });
}

export function useUploadCompanyLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, version }: { file: File; version: number }) =>
      uploadCompanyLogo(file, version),
    onSuccess: (company) => queryClient.setQueryData(settingsKeys.company, company),
  });
}

export function useBankAccounts(includeArchived: boolean) {
  return useQuery({
    queryKey: settingsKeys.bankAccountList(includeArchived),
    queryFn: () => listBankAccounts(includeArchived),
  });
}

export function useSaveBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ input, existing }: SaveVariables<BankAccountInput>) =>
      existing
        ? updateBankAccount(existing.id, input, existing.version)
        : createBankAccount(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: settingsKeys.bankAccounts }),
  });
}

export function useArchiveBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived, version }: ArchiveVariables) =>
      setBankAccountArchived(id, archived, version),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: settingsKeys.bankAccounts }),
  });
}

export function useLuts() {
  return useQuery({ queryKey: settingsKeys.luts, queryFn: listLuts });
}

export function useSaveLut() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ input, existing }: SaveVariables<LutInput>) =>
      existing ? updateLut(existing.id, input, existing.version) : createLut(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: settingsKeys.luts }),
  });
}
